"use client";

import { motion } from "framer-motion";
import { Users2, Code, Layout, ShieldCheck, ArrowRight, Sparkles } from "lucide-react";
import Link from "next/link";

export default function AboutPage() {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.2 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5 } }
  };

  return (
    <main className="min-h-screen pt-32 px-6 mb-12 md:px-12 max-w-7xl mx-auto bg-background">
      
      <section className="relative pt-10 pb-20 max-w-7xl mx-auto z-10">
        <motion.div 
          initial="hidden"
          animate="visible"
          variants={containerVariants}
          className="space-y-6"
        >
          <motion.div variants={itemVariants} className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-fd-500/10 border border-fd-500/20">
            <Sparkles size={14} className="text-fd-500 animate-pulse" />
            <span className="text-fd-500 text-[14px] font-bold uppercase tracking-widest">รู้จักกับเรา</span>
          </motion.div>

          <motion.h1 variants={itemVariants} className="text-4xl md:text-5xl font-bold leading-[1.1] uppercase">
            เราคือ <span className="text-fd-500 italic">ทีมผู้พัฒนา</span> <br />
            ที่ร่วมสร้างอนาคตให้ธุรกิจคุณ
          </motion.h1>

          <motion.p variants={itemVariants} className="md:text-lg text-foreground/60 leading-relaxed max-w-3xl border-l-2 border-fd-500/30 pl-6">
            บริษัท โฟลด้า จำกัด เราคือทีมงานสายเทคที่รวมตัวกันเพราะอยากสร้างงานที่ใช้งานได้จริง (Workable Software) เราให้ความสำคัญกับการวางโครงสร้างให้แข็งแรงตั้งแต่แรก เพื่อให้ระบบเติบโตไปพร้อมกับธุรกิจของคุณได้โดยไม่ต้องรื้อทำใหม่บ่อยๆ
          </motion.p>
          <motion.p variants={itemVariants} className="md:text-lg text-foreground/60 leading-relaxed max-w-3xl border-l-2 border-fd-500/30 pl-6">
            เราเชื่อว่าซอฟต์แวร์ที่ดีไม่ได้เกิดจากแค่การเขียนโค้ด แต่เกิดจากการทำความเข้าใจผู้ใช้งานและธุรกิจอย่างแท้จริง ทีมงานของเราจึงให้ความสำคัญกับการสื่อสารที่ชัดเจนและการวางแผนที่รอบคอบ เพื่อให้มั่นใจว่าโซลูชันที่เราพัฒนาขึ้นมาตรงกับความต้องการและช่วยขับเคลื่อนธุรกิจของลูกค้าไปข้างหน้าอย่างยั่งยืน
          </motion.p>
        </motion.div>
      </section>

      <section className="py-16 relative bg-foreground/[0.02]">
        <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-16 items-center">
          <motion.div 
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="space-y-8"
          >
            <h2 className="text-3xl font-bold uppercase tracking-tight">ทำไมต้องเลือกทีมของเรา?</h2>
            <div className="space-y-6">
              {[
                { icon: <ShieldCheck size={18} />, title: "ประสบการณ์ตรง", desc: "ทีม Developer ของเราผ่านงานโปรเจกต์ระดับองค์กรมาหลากหลายรูปแบบ" },
                { icon: <Code size={18} />, title: "เทคโนโลยีที่ทันสมัย", desc: "เราเลือกใช้เครื่องมือที่ดีที่สุด เพื่อความเร็วและความปลอดภัยสูงสุด" },
                { icon: <Users2 size={18} />, title: "การสื่อสารที่ชัดเจน", desc: "คุยง่าย ไม่ใช้ศัพท์เทคนิคที่เข้าใจยาก รายงานคืบหน้าสม่ำเสมอ" }
              ].map((item, i) => (
                <div key={i} className="flex gap-5 group">
                  <div className="mt-1 bg-fd-500 rounded-xl p-2 h-fit group-hover:rotate-12 transition-transform shadow-lg shadow-fd-500/20">
                    <div className="text-white">{item.icon}</div>
                  </div>
                  <div>
                    <h4 className="font-bold md:text-lg mb-1">{item.title}</h4>
                    <p className="md:text-lg text-foreground/60 leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
          
          <motion.div 
            whileHover={{ scale: 1.02 }}
            className="p-10 md:p-14 bg-background bg-secondary/30 border border-border rounded-[3rem] relative overflow-hidden"
          >
            <div className="absolute top-0 right-0 p-6 opacity-[0.03]">
              <Code size={120} />
            </div>
            <h3 className="text-fd-500 font-bold text-2xl mb-6 italic leading-tight">
              "เราเชื่อว่าซอฟต์แวร์ที่ดี เริ่มต้นจากโครงสร้างที่ถูกต้อง"
            </h3>
            <p className="text-foreground/50 leading-relaxed font-medium">
              ทีมงาน FOLDA ให้ความสำคัญกับการวางแผนสถาปัตยกรรม (Architecture) ก่อนเริ่มลงมือเขียนโค้ดเสมอ 
              เพื่อลดปัญหาการแก้ไขในระยะยาว และช่วยประหยัดงบประมาณของลูกค้าได้จริง
            </p>
          </motion.div>
        </div>
      </section>

      <section className="py-16 max-w-7xl mx-auto text-center">
        <h2 className="text-lg font-black text-fd-500 uppercase tracking-[0.5em] mb-12">Services</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[
            { icon: <Layout />, title: "Web App", desc: "พัฒนาเว็บไซต์ที่รองรับการใช้งานทุกอุปกรณ์" },
            { icon: <Code />, title: "Custom Soft", desc: "ออกแบบโปรแกรมเฉพาะทางที่ตอบโจทย์ธุรกิจ" },
            { icon: <ShieldCheck />, title: "Consulting", desc: "ให้คำปรึกษาด้านเทคนิคเพื่อปรับปรุงระบบเดิม" }
          ].map((service, i) => (
            <motion.div 
              key={i}
              whileHover={{ y: -10 }}
              className="p-10 rounded-[2.5rem] bg-secondary/30 border border-border hover:bg-fd-500/[0.02] hover:border-fd-500/20 transition-all duration-300 group"
            >
              <div className="mb-5 text-foreground/30 group-hover:text-fd-500 transition-colors inline-block">
                {service.icon}
              </div>
              <h4 className="text-xl font-bold mb-3 uppercase tracking-tighter">{service.title}</h4>
              <p className="text-[16px] text-foreground/50 leading-relaxed uppercase tracking-widest">{service.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

    </main>
  );
}